<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { collection, query, orderBy, limit, getDocs, where, Timestamp } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Newspaper, Megaphone, Sparkles, AlertTriangle, Info, Loader2 } from 'lucide-vue-next'

interface NewsItem {
  id: string
  title: string
  content: string
  type: 'announcement' | 'feature' | 'maintenance' | 'tip'
  publishedAt: Date
  expiresAt?: Date
  pinned?: boolean
}

const news = ref<NewsItem[]>([])
const isLoading = ref(true)

const typeConfig = {
  announcement: {
    label: 'Anuncio',
    icon: Megaphone,
    color: 'bg-blue-500/10 text-blue-600',
    badgeVariant: 'default' as const,
  },
  feature: {
    label: 'Nueva Funcionalidad',
    icon: Sparkles,
    color: 'bg-green-500/10 text-green-600',
    badgeVariant: 'secondary' as const,
  },
  maintenance: {
    label: 'Mantenimiento',
    icon: AlertTriangle,
    color: 'bg-yellow-500/10 text-yellow-600',
    badgeVariant: 'outline' as const,
  },
  tip: {
    label: 'Tip',
    icon: Info,
    color: 'bg-purple-500/10 text-purple-600',
    badgeVariant: 'secondary' as const,
  },
}

const formatDate = (date: Date): string => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (days === 0) return 'Hoy'
  if (days === 1) return 'Ayer'
  if (days < 7) return `Hace ${days} días`

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  }).format(date)
}

onMounted(async () => {
  try {
    // Cargar noticias de la colección 'news'
    // Solo mostrar las que no han expirado y ordenadas por fecha
    const now = Timestamp.now()
    const newsQuery = query(
      collection(db, 'news'),
      orderBy('publishedAt', 'desc'),
      limit(20)
    )
    const snapshot = await getDocs(newsQuery)

    const items: NewsItem[] = []
    snapshot.forEach((doc) => {
      const data = doc.data()
      const expiresAt = data.expiresAt?.toDate?.()

      // Filtrar expiradas en cliente (Firestore no soporta bien comparaciones de fecha con orderBy)
      if (!expiresAt || expiresAt > new Date()) {
        items.push({
          id: doc.id,
          title: data.title,
          content: data.content,
          type: data.type || 'announcement',
          publishedAt: data.publishedAt?.toDate?.() || new Date(),
          expiresAt,
          pinned: data.pinned || false,
        })
      }
    })

    // Ordenar: pinned primero, luego por fecha
    news.value = items.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return b.publishedAt.getTime() - a.publishedAt.getTime()
    })
  } catch (error) {
    console.error('Error loading news:', error)
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div>
    <div class="flex items-center gap-2 mb-6">
      <Newspaper class="h-6 w-6 text-primary" />
      <h2 class="text-2xl font-bold">Noticias y Anuncios</h2>
    </div>

    <p class="text-muted-foreground mb-6">
      Mantente al día con las últimas novedades, actualizaciones y consejos de la plataforma.
    </p>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
    </div>

    <!-- Empty State -->
    <div v-else-if="news.length === 0" class="text-center py-12">
      <div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
        <Newspaper class="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 class="text-lg font-semibold mb-2">No hay noticias</h3>
      <p class="text-muted-foreground">
        Las nuevas actualizaciones y anuncios aparecerán aquí.
      </p>
    </div>

    <!-- News List -->
    <div v-else class="space-y-4">
      <Card
        v-for="item in news"
        :key="item.id"
        :class="item.pinned ? 'border-primary/50 bg-primary/5' : ''"
      >
        <CardHeader class="pb-3">
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-center gap-3">
              <div
                class="h-10 w-10 rounded-lg flex items-center justify-center"
                :class="typeConfig[item.type].color"
              >
                <component :is="typeConfig[item.type].icon" class="h-5 w-5" />
              </div>
              <div>
                <div class="flex items-center gap-2 mb-1">
                  <Badge :variant="typeConfig[item.type].badgeVariant" class="text-xs">
                    {{ typeConfig[item.type].label }}
                  </Badge>
                  <Badge v-if="item.pinned" variant="outline" class="text-xs">
                    Fijado
                  </Badge>
                </div>
                <CardTitle class="text-base">{{ item.title }}</CardTitle>
              </div>
            </div>
            <span class="text-xs text-muted-foreground whitespace-nowrap">
              {{ formatDate(item.publishedAt) }}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <p class="text-sm text-muted-foreground whitespace-pre-line">
            {{ item.content }}
          </p>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
